import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Participant } from '@models/Sessions/Participant';
import { Session } from '@models/Sessions/Session';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationService } from '@services/auth/authentication.service';
import { BusService } from '@services/bus.service';
import {
  ConfigService,
  IInputLengths,
  IRoleSelectOption,
} from '@services/config.service';
import { EventsService } from '@services/events.service';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';

import { TrackingService } from '../../../../services/tracking/tracking.service';

@Component({
  selector: 'app-sessions-new-topic-modal',
  templateUrl: './new-topic-modal.component.html',
  styleUrls: ['./new-topic-modal.component.scss'],
})
export class NewTopicModalComponent implements OnInit, OnDestroy {
  inputLengths: IInputLengths;
  roles: IRoleSelectOption[];

  sessionForm: UntypedFormGroup;
  newEmailControl = new UntypedFormControl('', [Validators.email]);
  pending = false;
  activeUpload = false;
  error = undefined;

  selectedTeamIndex = 0;
  selectedParticipant: Participant;

  openNewIdeaModal = false;

  newTeam = {
    name: '',
    _id: '',
  };

  newParticipant = {
    name: '',
    email: '',
    votes_for_lookup: [],
    role: 2,
    _id: '',
  } as any;

  public session: Session;

  constructor(
    public activeModal: NgbActiveModal,
    private configService: ConfigService,
    private authService: AuthenticationService,
    private bus: BusService,
    private events: EventsService,
    private sessionRequestService: SessionsRequestService,
    private trackingService: TrackingService,
    private router: Router,
  ) {
    this.inputLengths = configService.inputLengths;
    this.roles = configService.rolesSelectOptions;

    this.sessionForm = new UntypedFormGroup({
      title: new UntypedFormControl('', [
        Validators.required,
        Validators.maxLength(this.inputLengths.SESSION_TITLE),
      ]),
      oneLiner: new UntypedFormControl('', [
        Validators.maxLength(this.inputLengths.SESSION_ONE_LINER),
      ]),
      description: new UntypedFormControl('', [
        Validators.maxLength(this.inputLengths.SESSION_DESCRIPTION),
      ]),
    });

    this.newEmailControl.setValidators([
      Validators.required,
      Validators.email,
      Validators.maxLength(this.inputLengths.EMAIL),
    ]);
    this.session = {
      config: {
        cluster: false,
        matrix: {
          active: false,
          role_view: {
            moderators: false,
            participants: false,
            externals: false,
          },
        },
        voting: { active: true },
        ideas: { active: true },
        comments: { active: true },
        participants: [
          {
            role: this.authService.hasRole('admin') ? 0 : 1,
            email: this.authService.currentUserSync.email,
            votes_for_lookup: [],
            team_idx: 0,
            _id: this.authService.currentUserSync._id,
            team: null,
            votes_for: [],
          },
        ],
        teams: [],
        thumbnail_id: '',
        title: '',
        one_liner: '',
        description: '',
        axis: {
          x: null,
          y: null,
        },
        morphbox: { active: false }, // to delete
        random_images: { active: false }, // to delete
        random_words: { active: false }, // to delete
        people: { active: true },
        workspace: { active: false },
        teams_page: { active: false }, // to delete
        personal_results: { active: false }, // to delete
        pdf_exports: { active: false },
        solutions: false,
        benefits_and_challenges: false,
      },
      created_at: null,
      created_by: null,
      data: {
        announcements: [],
        ideas: [],
        followers: [],
        morphboxes: [],
        notifications: [],
      },
      scores: null,
      i_follow: false,
      me: null,
      _id: null,
      lockActions: false,
      canRevoke: false,
    };
  }

  dismiss() {
    this.activeModal.dismiss();
  }

  postSession() {
    if (this.sessionForm.valid) {
      this.pending = true;

      this.session.config = {
        ...this.session.config,
        title: this.sessionForm.controls.title.value,
        one_liner: this.sessionForm.controls.oneLiner.value,
        description: this.sessionForm.controls.description.value,
      };

      this.bus.publish(
        this.events.requested.action.session.create,
        this.session,
      );

      this.bus.publish(
        this.events.requested.action.tracking.track,
        this.session,
        'session',
        'create',
        this.session.config.title,
      );
    }
  }

  sessionCreateSuccess(session) {
    this.activeModal.close();

    if (this.openNewIdeaModal) {
      this.router.navigate(['topics', session._id], {
        queryParams: { newIdea: true },
      });
    } else {
      this.pending = false;
      this.sessionRequestService.getAll().subscribe();
    }
  }

  sessionCreateFailure(error) {
    this.pending = false;
    this.error = error;
  }

  addTeam() {
    if (this.newTeam.name.length > 0) {
      this.session.config.teams.push(this.newTeam);
      this.newTeam = { name: '', _id: undefined };

      this.selectedTeamIndex = this.session.config.teams.length - 1;
      this.selectedParticipant = undefined;
    }
  }

  selectTeam(index: number) {
    this.selectedTeamIndex = index;
    this.selectedParticipant = undefined;
  }

  deleteTeam(index: number) {
    if (
      confirm('Are you sure you want to delete this team and all its members?')
    ) {
      this.session.config.teams.splice(index, 1);

      const teamIndex = this.session.config.participants.findIndex(
        part => part.team_idx === index,
      );
      this.session.config.participants.splice(teamIndex, 1);

      for (const part of this.session.config.participants) {
        part.votes_for_lookup.splice(index, 1);
      }

      this.selectedTeamIndex = 0;
      this.selectedParticipant = undefined;
    }
  }

  addParticipant() {
    if (this.newEmailControl.valid) {
      this.newParticipant.email = this.newEmailControl.value;
      if (
        this.session.config.participants.find(
          part => part.email === this.newParticipant.email,
        )
      ) {
        console.error('User already added');
        return;
      }

      this.newParticipant.team_idx = this.selectedTeamIndex;
      this.session.config.participants.push(this.newParticipant);
      this.selectedParticipant = this.newParticipant;
      this.newParticipant = {
        email: '',
        role: 2,
        votes_for_lookup: [],
        team_idx: undefined,
        name: '',
        _id: undefined,
      };
      this.newEmailControl.reset();
    }
  }

  selectParticipant(participant: Participant) {
    this.selectedParticipant = participant;
  }

  deleteParticipant(participant: Participant) {
    if (confirm('Are you sure you want to delete this participant?')) {
      const index = this.session.config.participants.findIndex(
        p => p.email === participant.email,
      );
      this.session.config.participants.splice(index, 1);
      this.selectedParticipant = undefined;
    }
  }

  get selectedTeamParticipants() {
    return this.session.config.participants.filter(
      part => part.team_idx === this.selectedTeamIndex,
    );
  }

  get tooLongOneLiner() {
    return (
      this.sessionForm.controls.oneLiner.value.length >
      this.inputLengths.SESSION_ONE_LINER
    );
  }

  onUploadStart() {
    this.activeUpload = true;
  }

  onUploadComplete(key: string) {
    this.activeUpload = false;
    this.session.config.thumbnail_id = key;
  }

  ngOnInit() {
    this.subscribe();
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  subscribe() {
    this.bus.subscribe(
      this.events.received.action.session.create.success,
      this.sessionCreateSuccess.bind(this),
    );
    this.bus.subscribe(
      this.events.received.action.session.create.failure,
      this.sessionCreateFailure.bind(this),
    );
  }

  unsubscribe() {
    this.bus.unsubscribe(
      this.events.received.action.session.create.success,
      this.sessionCreateSuccess.bind(this),
    );
    this.bus.unsubscribe(
      this.events.received.action.session.create.failure,
      this.sessionCreateFailure.bind(this),
    );
  }
}
